import { computed, ref } from 'vue'
import type { ColorScheme } from '@threadline/shared'
import { changePreferredColorScheme, useAuthenticationSession } from './useAuthenticationSession.ts'

/**
 * Light, dark, or whatever the device says.
 *
 * **The preference belongs to the account, not to the browser.** Two accounts open in two
 * tabs are two different people: a preference kept in web storage had one of them deciding
 * for the other, which is exactly the bug this shape exists to prevent. Each tab reads the
 * account it is acting as, so the two can differ on the same screen.
 *
 * `auto` is a real choice rather than a missing one. Somebody who never opens settings gets
 * the scheme their operating system already switches on at dusk, and somebody who overrides
 * it keeps that override even when the device changes its mind.
 */
export type ColorSchemePreference = 'auto' | ColorScheme

export const COLOR_SCHEME_PREFERENCES: readonly ColorSchemePreference[] = ['auto', 'light', 'dark']

const DARK_SCHEME_MEDIA_QUERY = '(prefers-color-scheme: dark)'

export const LIGHT_THEME_NAME = 'threadlineLight'
export const DARK_THEME_NAME = 'threadlineDark'

function detectDevicePrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_SCHEME_MEDIA_QUERY).matches
  )
}

const devicePrefersDarkReference = ref(detectDevicePrefersDark())

/** Which theme is actually in force, once the preference and the device are both read. */
export function resolveThemeName(
  preference: ColorSchemePreference,
  devicePrefersDark: boolean,
): string {
  if (preference === 'auto') {
    return devicePrefersDark ? DARK_THEME_NAME : LIGHT_THEME_NAME
  }
  return preference === 'dark' ? DARK_THEME_NAME : LIGHT_THEME_NAME
}

let deviceListenerIsAttached = false

/**
 * Follows the device for as long as the preference is `auto`. The listener is attached once
 * and never removed: it belongs to the application rather than to any screen, and an
 * operating system switching to dark at sunset should reach a tab that has been open since
 * morning.
 */
function watchDeviceColorScheme(): void {
  if (deviceListenerIsAttached || typeof window === 'undefined') {
    return
  }
  if (typeof window.matchMedia !== 'function') {
    return
  }
  deviceListenerIsAttached = true
  window.matchMedia(DARK_SCHEME_MEDIA_QUERY).addEventListener('change', (mediaQueryEvent) => {
    devicePrefersDarkReference.value = mediaQueryEvent.matches
  })
}

export function useColorScheme() {
  watchDeviceColorScheme()
  const { authenticatedUser } = useAuthenticationSession()

  // Nobody signed in yet — the sign-in screen — follows the device, which is the only
  // answer knowable before an account is.
  const colorSchemePreference = computed<ColorSchemePreference>(
    () => authenticatedUser.value?.preferredColorScheme ?? 'auto',
  )
  const activeThemeName = computed(() =>
    resolveThemeName(colorSchemePreference.value, devicePrefersDarkReference.value),
  )
  const isDark = computed(() => activeThemeName.value === DARK_THEME_NAME)

  async function chooseColorScheme(preference: ColorSchemePreference): Promise<void> {
    await changePreferredColorScheme(preference === 'auto' ? null : preference)
  }

  return { colorSchemePreference, activeThemeName, isDark, chooseColorScheme }
}
